import BaseBehavior from '../../../framework/base/base';
import { whenInView } from '../../../shared/utilities/when-in-view';
import { debounce } from '../../../shared/utilities/debounce';
import { showElement, hideElement } from '../../../shared/utilities/show-hide';
import * as graphUtil from './graph-util';

const currentYear = new Date().getFullYear();

const chartConstants = {
	tooltipMaxWidth: 170,
	chartHeight: 345
};

export default class InvestmentCalculator extends BaseBehavior {
	constructor(element) {
		super(element);
		this.initDomCache();
		this.addEventListeners();
		this.initValues();
		this.fetchChartData();
		this.formatInitalAndMonthlyAmount();
		whenInView(element).then(() => this.loadAndRenderChart());
		this.isRTL = document.dir === 'rtl';
	}

	loadAndRenderChart() {
		import(/* webpackChunkName: "highcharts" */ 'highcharts')
			.then(Highcharts => {
				return import(
					/* webpackChunkName: "accessibility" */ 'highcharts/modules/accessibility'
				).then(module => {
					module.default(Highcharts);
					this.initChart(Highcharts);
				});
			})
			.catch(e => console.error(e));
	}

	initChart(Highcharts) {
		const chartOptions = graphUtil.getChartOptions(
			this.propsForGraph,
			currentYear,
			this.dom.yearInput.value,
			this.returnValues,
			this.totalDeposit,
			this.isRTL
		);
		this.chart = Highcharts.chart(this.dom.graph, chartOptions);
	}

	resetToNumberFromAmount(type) {
		const amountString = this.dom[type].value;
		return Number(this.reverseFormatNumber(amountString, this.culture));
	}

	pushCalculatorDetailsForTracking() {
		const startingAmount = Number(
			this.reverseFormatNumber(this.dom.initialAmount.value, this.culture)
		);
		const monthlyAmount = Number(
			this.reverseFormatNumber(this.dom.monthlyAmount.value, this.culture)
		);
		const returnRate = Number(this.dom.rateInput.value);
		const timeHorizon = Number(this.dom.yearInput.value);
		// Construct the event data structure
		const dataLayerEvent = {
			event: 'investmentCalculatorEngagement',
			calculatorInfo: {
				startingAmount,
				monthlyAmount,
				investmentCurrency: this.currencyUnit,
				returnRate,
				timeHorizon
			}
		};
		// Push event to Adobe Data Layer
		if (window.adobeDataLayer) {
			window.adobeDataLayer.push(dataLayerEvent);
		} else {
			console.error('Adobe Data Layer not found.');
		}
	}

	isValidValue(value) {
		return value.match(/\d/g).length <= 8;
	}

	checkForDigitsLimit(elem) {
		if (this.isValidValue(elem.value)) {
			hideElement(elem.nextElementSibling);
		}
	}

	hideValidationMessages() {
		hideElement(this.dom.initialAmount.nextElementSibling);
		hideElement(this.dom.monthlyAmount.nextElementSibling);
	}

	addEventListeners() {
		this.lastRate = this.dom.rateInput.value;
		this.lastYear = this.dom.yearInput.value;

		const debouncedInputHandler = debounce(() => {
			this.updateCalculator();
			this.pushCalculatorDetailsForTracking();
		}, 500);
		this.addEventListener(this.dom.rateInput, 'input', () => {
			this.hideValidationMessages(); // Added to fix issue in mobile view
			if (this.lastRate !== this.dom.rateInput.value) {
				this.changeRate();
				debouncedInputHandler();
				this.lastRate = this.dom.rateInput.value;
			}
		});
		this.addEventListener(this.dom.yearInput, 'input', () => {
			this.hideValidationMessages(); // Added to fix issue in mobile view
			if (this.lastYear !== this.dom.yearInput.value) {
				this.changeYear();
				debouncedInputHandler();
				this.lastYear = this.dom.yearInput.value;
			}
		});

		this.addEventListener(this.dom.initialAmount, 'focusout', evt => {
			this.checkForDigitsLimit(evt.target);
		});

		this.addEventListener(this.dom.monthlyAmount, 'focusout', evt => {
			this.checkForDigitsLimit(evt.target);
		});
		this.addEventListener(this.dom.initialAmount, 'input', evt => {
			this.formatWithType(evt);

			debouncedInputHandler();
		});
		this.addEventListener(this.dom.monthlyAmount, 'input', evt => {
			this.formatWithType(evt);
			debouncedInputHandler();
		});
		this.addEventListener(this.dom.form, 'submit', e => e.preventDefault());
		window.addEventListener(
			'resize',
			debounce(() => this.updateCalculator(), 500)
		);
	}

	formatWithType(evt) {
		const self = evt.target;
		self.value = parseInt(self.value.replace(/\D/g, ''), 10);
		if (!Number.isNaN(+self.value)) {
			if (this.isValidValue(self.value)) {
				hideElement(self.nextElementSibling);
				const value = parseInt(self.value, 10);
				self.value = graphUtil.getFormatedAmount(value, this.culture);
			} else {
				showElement(self.nextElementSibling);
				let value = self.value.slice(0, self.value.length - 1); // keeping only 8 digits(existing length)
				value = parseInt(value, 10);
				self.value = graphUtil.getFormatedAmount(value, this.culture);
			}
		} else {
			self.value = 0; // To prevent showing NaN in the input field and in the graph
		}
	}

	updateCalculator() {
		this.fetchChartData();
		this.updateChart();
	}

	updateChart() {
		const initialAmount = this.resetToNumberFromAmount('initialAmount');
		const chartOptions = graphUtil.getUpdatedChartOptions(
			this.propsForGraph,
			currentYear,
			this.dom.yearInput.value,
			this.returnValues,
			this.totalDeposit,
			initialAmount
		);
		this.chart.update(chartOptions);
		return true;
	}

	reverseFormatNumber(stringNumber, locale) {
		const thousandSeparator = Intl.NumberFormat(locale)
			.format(11111)
			.replace(/\p{Number}/gu, '');
		const decimalSeparator = Intl.NumberFormat(locale)
			.format(1.1)
			.replace(/\p{Number}/gu, '');

		return parseFloat(
			stringNumber
				.replace(new RegExp('\\' + thousandSeparator, 'g'), '')
				.replace(new RegExp('\\' + decimalSeparator), '.')
		);
	}

	changeNumberToText(elem) {
		const amount = this.reverseFormatNumber(elem.value, this.culture); // for resetting it to original value(in case user come to this page through back button)
		elem.value = graphUtil.getFormatedAmount(amount, this.culture);
	}

	formatInitalAndMonthlyAmount() {
		this.changeNumberToText(this.dom.initialAmount);
		this.changeNumberToText(this.dom.monthlyAmount);
	}

	changeRate() {
		this.dom.rateValue.textContent = this.dom.rateInput.value;
	}

	changeYear() {
		this.dom.yearValue.textContent = this.dom.yearInput.value;
	}

	initValues() {
		this.chartTextValues = graphUtil.fetchChartTextValues(this.element.dataset);
		this.colors = {
			backgroundColor: this.element.dataset.chartBackgroundColor,
			returnsColor: this.element.dataset.returnsGraphColor,
			depositColor: this.element.dataset.depositGraphColor
		};
		this.currencyUnit = this.element.dataset.currencyUnit || 'DKK';
		this.culture = this.element.dataset.culture || 'da-dk';
		this.yAxisTicks = Number(this.element.dataset.yaxisTicks) || 5;
		this.propsForGraph = {
			yAxisTicks: this.yAxisTicks,
			colors: this.colors,
			chartTextValues: this.chartTextValues,
			chartConstants
		};
	}

	fetchChartData() {
		const chartData = this.computeValues(this.dom);
		this.returnValues = chartData.returnValues;
		this.totalDeposit = chartData.totalDeposit;
		this.updateTextContent();
	}

	updateTextContent() {
		const { returnValues } = this;
		const DepositValues = this.totalDeposit;
		const returnedValue = returnValues[returnValues.length - 1];
		const totalDeposit = DepositValues[DepositValues.length - 1];
		const { currencyUnit, culture } = this.element.dataset;

		this.dom.totalSavingValue.textContent = graphUtil.getFormatedAmount(
			returnedValue,
			culture,
			currencyUnit
		);
		this.dom.totalSavings.textContent = graphUtil.getFormatedAmount(
			returnedValue - totalDeposit,
			culture,
			currencyUnit
		);
		this.dom.totalDeposits.textContent = graphUtil.getFormatedAmount(
			totalDeposit,
			culture,
			currencyUnit
		);
	}

	computeValues(dom) {
		const returnValues = [];
		const totalDeposit = [];
		const yearValue = Number(dom.yearValue.textContent);
		const monthlyAmount = this.resetToNumberFromAmount('monthlyAmount');
		const initialAmount = this.resetToNumberFromAmount('initialAmount');
		const returnRate = Number(dom.rateInput.value);
		const periodicReturnRate = (1 + returnRate / 100) ** (1 / 12) - 1;
		for (let i = 0; i <= yearValue; i++) {
			let monthlyReturn = 0;
			if (monthlyAmount > 0) {
				monthlyReturn = this.getReturn(monthlyAmount, i, periodicReturnRate);
			}
			const returnAmountForInitialAmount = initialAmount * (1 + returnRate / 100) ** i;
			returnValues.push(Math.round(returnAmountForInitialAmount + monthlyReturn));
			totalDeposit.push(Math.round(initialAmount + monthlyAmount * i * 12));
		}

		return {
			returnValues,
			totalDeposit
		};
	}

	getReturn(monthlyAmount, year, rate) {
		// M = P × ({[1 + i]^n – 1} / i) × (1 + i).
		const i = rate;
		const n = year * 12;
		const multiplier = ((1 + i) ** n - 1) / i;
		const FV = monthlyAmount * multiplier * (1 + i);
		return FV;
	}
}
